import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { RiskTolerability } from '../types';
import { riskService } from '../riskService';
import { hazardService } from '../../hazards/hazardService';
import { Hazard } from '../../hazards/types';
import { getActiveSessionOrganizationCode } from '../../../../shared/api';
import { useEscapeKey } from '../../../../shared/useGlobalModalEscape';
import { useDialog } from '../../../../shared/DialogContext';

interface CreateRiskModalProps {
  onClose: () => void;
  onCreated: () => void;
}

const SEVERITY_OPTIONS = [
  { value: 5, label: '5 - Catastrophic', desc: 'Equipment destroyed, multiple fatalities' },
  { value: 4, label: '4 - Major', desc: 'Serious injury, major equipment damage' },
  { value: 3, label: '3 - Moderate', desc: 'Injury to persons, significant damage' },
  { value: 2, label: '2 - Minor', desc: 'Minor incident, operating limitations' },
  { value: 1, label: '1 - Negligible', desc: 'Inconvenience, normal operating limits' },
];

const LIKELIHOOD_OPTIONS = [
  { value: 'A', label: 'A - Frequent', desc: 'Likely to occur many times' },
  { value: 'B', label: 'B - Probable', desc: 'Likely to occur sometimes' },
  { value: 'C', label: 'C - Remote', desc: 'Unlikely, but possible to occur' },
  { value: 'D', label: 'D - Extremely Improbable (Unlikely)', desc: 'Very unlikely to occur' },
  { value: 'E', label: 'E - Extremely Improbable', desc: 'Almost inconceivable' },
];

export const CreateRiskModal: React.FC<CreateRiskModalProps> = ({ onClose, onCreated }) => {
  useEscapeKey(onClose);
  const { showError, showWarning } = useDialog();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [department, setDepartment] = useState('Flight Operations');
  const [ownerName, setOwnerName] = useState('Safety Manager');
  const [reviewFrequencyDays, setReviewFrequencyDays] = useState(90);
  const [selectedHazardId, setSelectedHazardId] = useState('');
  const [hazards, setHazards] = useState<Hazard[]>([]);

  // Assessment
  const [severity, setSeverity] = useState<number>(3);
  const [likelihood, setLikelihood] = useState<string>('C');
  const [loading, setLoading] = useState(false);

  // Load available hazards for linking
  useEffect(() => {
    hazardService.getHazards({ limit: 100 })
      .then((res) => {
        setHazards(res.items || []);
      })
      .catch((err) => {
        console.warn('Could not load hazards for linking:', err);
      });
  }, []);

  // Compute Tolerability per ICAO Doc 9859
  const computeTolerability = (sev: number, lik: string): RiskTolerability => {
    const code = `${sev}${lik}`;
    const intolerable = ['5A', '5B', '5C', '4A', '4B', '3A'];
    const tolerable = ['5D', '5E', '4C', '4D', '4E', '3B', '3C', '3D', '2A', '2B', '2C'];
    if (intolerable.includes(code)) return 'INTOLERABLE';
    if (tolerable.includes(code)) return 'TOLERABLE';
    return 'ACCEPTABLE';
  };

  const currentRiskIndex = `${severity}${likelihood}`;
  const currentTolerability = computeTolerability(severity, likelihood);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      showWarning('Risk title is required.', 'Validation Error');
      return;
    }

    try {
      setLoading(true);
      const chosenHazard = hazards.find((h) => h.id === selectedHazardId);

      await riskService.createRisk({
        organizationId: getActiveSessionOrganizationCode() || undefined,
        title: title.trim(),
        description: description.trim() || undefined,
        hazardId: selectedHazardId || undefined,
        hazardNumber: chosenHazard?.hazardNumber || undefined,
        hazardTitle: chosenHazard?.title || undefined,
        initialSeverity: severity,
        initialLikelihood: likelihood,
        department: department.trim() || undefined,
        ownerName: ownerName.trim() || undefined,
        reviewFrequencyDays,
        createdBy: 'SAFETY_OFFICER',
      });

      onCreated();
    } catch (err: any) {
      showError(err, 'Failed to Create Risk');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-card modal-lg">
        {/* Header */}
        <div className="modal-header">
          <div className="modal-header-content">
            <h3>Register & Assess Safety Risk</h3>
            <p>ICAO Doc 9859 5x5 Safety Risk Evaluation and Baseline Tolerability Determination</p>
          </div>
          <button type="button" onClick={onClose} className="modal-close-btn" title="Kapat" aria-label="Kapat">
            <X size={20} />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {/* General Information */}
            <div className="form-field">
              <label>Risk Title *</label>
              <input
                type="text"
                required
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Unstabilized approach risk during severe tailwind at LTFM"
              />
            </div>

            <div className="form-field">
              <label>Detailed Description & Scenarios</label>
              <textarea
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe potential outcomes, operational exposure, operational consequences, and contributing factors..."
              />
            </div>

            <div className="form-grid-2">
              <div className="form-field">
                <label>Linked Safety Hazard</label>
                <select
                  value={selectedHazardId}
                  onChange={(e) => setSelectedHazardId(e.target.value)}
                >
                  <option value="">-- No direct hazard link --</option>
                  {hazards.map((h) => (
                    <option key={h.id} value={h.id}>
                      {h.hazardNumber} - {h.title}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-field">
                <label>Responsible Department</label>
                <select
                  value={department}
                  onChange={(e) => setDepartment(e.target.value)}
                >
                  <option value="Flight Operations">Flight Operations</option>
                  <option value="Ground Operations">Ground Operations</option>
                  <option value="Maintenance & Engineering">Maintenance & Engineering</option>
                  <option value="Safety & Quality">Safety & Quality</option>
                  <option value="Cabin Crew Operations">Cabin Crew Operations</option>
                  <option value="Dispatch & OCC">Dispatch & OCC</option>
                </select>
              </div>
            </div>

            <div className="form-grid-2">
              <div className="form-field">
                <label>Risk Owner</label>
                <input
                  type="text"
                  value={ownerName}
                  onChange={(e) => setOwnerName(e.target.value)}
                />
              </div>

              <div className="form-field">
                <label>Review Frequency (Days)</label>
                <input
                  type="number"
                  min={1}
                  max={365}
                  value={reviewFrequencyDays}
                  onChange={(e) => setReviewFrequencyDays(parseInt(e.target.value) || 90)}
                />
              </div>
            </div>

            {/* Initial Risk Assessment Matrix Selector */}
            <div
              style={{
                border: '1px solid var(--border-color, #e2e8f0)',
                borderRadius: '8px',
                padding: '16px',
                background: 'var(--bg-subtle, #f8fafc)',
                display: 'flex',
                flexDirection: 'column',
                gap: '14px',
              }}
            >
              <h4 style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary, #0f172a)', margin: 0 }}>
                Baseline / Initial ICAO 5x5 Assessment
              </h4>

              <div className="form-grid-2">
                <div className="form-field">
                  <label>Severity (Consequence Magnitude)</label>
                  <select
                    value={severity}
                    onChange={(e) => setSeverity(parseInt(e.target.value))}
                  >
                    {SEVERITY_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label} - {opt.desc}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-field">
                  <label>Likelihood (Probability of Occurrence)</label>
                  <select
                    value={likelihood}
                    onChange={(e) => setLikelihood(e.target.value)}
                  >
                    {LIKELIHOOD_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label} - {opt.desc}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Calculated Risk Index & Tolerability Badge */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 16px',
                  borderRadius: '6px',
                  background: currentTolerability === 'INTOLERABLE' ? '#fee2e2' : currentTolerability === 'TOLERABLE' ? '#fef3c7' : '#dcfce7',
                  border: `1px solid ${currentTolerability === 'INTOLERABLE' ? '#fca5a5' : currentTolerability === 'TOLERABLE' ? '#fde68a' : '#86efac'}`,
                }}
              >
                <div>
                  <span style={{ fontSize: '11px', textTransform: 'uppercase', fontWeight: 700, color: '#475569' }}>
                    Resulting Initial Risk Index:
                  </span>
                  <span style={{ marginLeft: '8px', fontSize: '16px', fontWeight: 800, color: '#0f172a' }}>
                    {currentRiskIndex}
                  </span>
                </div>

                <div
                  style={{
                    background: currentTolerability === 'INTOLERABLE' ? '#ef4444' : currentTolerability === 'TOLERABLE' ? '#f59e0b' : '#10b981',
                    color: '#ffffff',
                    padding: '4px 12px',
                    borderRadius: '999px',
                    fontSize: '12px',
                    fontWeight: 700,
                    letterSpacing: '0.05em',
                  }}
                >
                  {currentTolerability}
                </div>
              </div>

              {currentTolerability === 'INTOLERABLE' && (
                <div style={{ fontSize: '12px', color: '#b91c1c', fontWeight: 500, lineHeight: 1.4 }}>
                  ⚠️ <strong>High Risk Warning:</strong> Under ICAO Doc 9859, operations with Intolerable risk cannot be accepted under normal authority. Immediate risk mitigation is required, and residual acceptance demands Accountable Manager sign-off.
                </div>
              )}
            </div>
          </div>

          {/* Modal Actions */}
          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary"
            >
              {loading ? 'Creating Risk...' : 'Register Safety Risk'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
